/**
 * B1084 (c) — the storefront's half of the `?cart=` handoff (design-seat
 * ruling 2026-10-27 19:4xZ, class A by recorded default; Pathik overrules
 * with "no on B1084 (c)"). An agent that built a cart hands a person
 * `/checkout?tenant=<t>&cart=<cho1 capability>`. The page boots as usual —
 * its OWN fresh guest session from the ordinary guest door — and then
 * spends the capability once through `cart.adopt`, which moves the cart's
 * fence to that session. The capability leaves the address bar at once (a
 * spent link must not sit in history or a bookmark). Pure: no React.
 */
import type { ApiClient } from "./api.js";
import { translateRefusal, type WireRefusal } from "./refusals.js";

/** The query parameter the continue_url carries the capability in. */
export const HANDOFF_PARAM = "cart";

const CAPABILITY = /^cho1\.[A-Za-z0-9_-]+\.[^.\s]+\.[A-Za-z0-9_-]+$/;

/** The capability on this page's address, when there is one (only cho1 bytes count). */
export function handoffTokenFrom(href: string): string | undefined {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return undefined;
  }
  const value = url.searchParams.get(HANDOFF_PARAM);
  return value !== null && CAPABILITY.test(value) ? value : undefined;
}

/** The same address without the capability — path, the other parameters and the fragment kept (for history.replaceState). */
export function hrefWithoutHandoff(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(HANDOFF_PARAM);
  const query = url.searchParams.toString();
  return `${url.pathname}${query === "" ? "" : `?${query}`}${url.hash}`;
}

/** The continue_url on this page's own origin, when the door named none (the stage knows no storefront host). */
export function continueUrlOn(origin: string, tenantId: string, token: string): string {
  return `${origin.replace(/\/+$/, "")}/checkout?tenant=${encodeURIComponent(tenantId)}&${HANDOFF_PARAM}=${token}`; // vocab-ok: a URL query parameter (B830 tenant addressing), never rendered as copy
}

export type HandoffOutcome = { readonly cartId: string; readonly agent?: string } | { readonly refused: string };

const isWire = (err: unknown): err is WireRefusal =>
  typeof err === "object" && err !== null && typeof (err as { message?: unknown }).message === "string" && typeof (err as { status?: unknown }).status === "number";

/** Spend the capability under the page's own session; a refusal comes back as buyer words, never the wire's. */
export async function redeemCartHandoff(api: Pick<ApiClient, "call">, token: string): Promise<HandoffOutcome> {
  try {
    const res = (await api.call("cart.adopt", { token })) as { cartId: string; agent?: string };
    return { cartId: res.cartId, ...(res.agent === undefined ? {} : { agent: res.agent }) };
  } catch (err) {
    return { refused: translateRefusal(isWire(err) ? err : { status: 0, code: "UNKNOWN", message: "", retryable: false }) };
  }
}
